/**
 * Highlight and text-colour marks with sanitised colours (#279).
 *
 * The stock extensions copy whatever colour string they are given straight
 * into a style attribute. Document JSON reaches the editor from the API and
 * the MCP server, not only from the colour picker, so both the parse and the
 * render path run the value through sanitizeColor() and drop anything that is
 * not a plain colour.
 *
 * Underline needs no registration here: StarterKit v3 already includes it
 * (with Mod-u), it simply had no toolbar button.
 */

import Highlight from '@tiptap/extension-highlight';
import { TextStyle, Color } from '@tiptap/extension-text-style';
import type { Editor } from '@tiptap/core';
import { sanitizeColor } from './formatColors';

export const SafeHighlight = Highlight.extend({
  addAttributes() {
    return {
      color: {
        default: null,
        parseHTML: (element: HTMLElement) =>
          sanitizeColor(element.getAttribute('data-color') || element.style.backgroundColor),
        renderHTML: (attributes: Record<string, unknown>) => {
          const color = sanitizeColor(attributes.color);
          if (!color) return {};
          // color: inherit keeps the theme's text colour on top of the tint.
          return { 'data-color': color, style: `background-color: ${color}; color: inherit` };
        },
      },
    };
  },
}).configure({ multicolor: true });

export const SafeColor = Color.extend({
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          color: {
            default: null,
            parseHTML: (element: HTMLElement) => sanitizeColor(element.style.color?.replace(/['"]+/g, '')),
            renderHTML: (attributes: Record<string, unknown>) => {
              const color = sanitizeColor(attributes.color);
              return color ? { style: `color: ${color}` } : {};
            },
          },
        },
      },
    ];
  },
});

/** Marks cleared by "clear formatting". Links are kept on purpose. */
export const CLEARABLE_MARKS = ['bold', 'italic', 'underline', 'strike', 'code', 'highlight', 'textStyle'] as const;

export const formattingExtensions = [TextStyle, SafeColor, SafeHighlight];

export function applyColor(editor: Editor, kind: 'highlight' | 'text', color: string | null): void {
  const chain = editor.chain().focus();
  if (kind === 'highlight') {
    (color ? chain.setHighlight({ color }) : chain.unsetHighlight()).run();
  } else {
    (color ? chain.setColor(color) : chain.unsetColor()).run();
    if (!color) dropEmptyStoredTextStyle(editor);
  }
}

/**
 * unsetColor with a collapsed cursor leaves a textStyle stored mark whose
 * attributes are all null (removeEmptyTextStyle only looks at the document),
 * and the next typed text is saved carrying that empty mark (#295). Drop it.
 */
function dropEmptyStoredTextStyle(editor: Editor): void {
  const { state } = editor;
  const stored = state.storedMarks;
  if (!stored) return;
  const empty = stored.find(
    (m) => m.type.name === 'textStyle' && Object.values(m.attrs).every((v) => v === null || v === undefined),
  );
  if (empty) editor.view.dispatch(state.tr.removeStoredMark(empty));
}

/**
 * The colour the toolbar should show as current.
 *
 * With a collapsed cursor this is what the next typed character will get:
 * the stored marks when there are any, else the marks at the cursor. Plain
 * editor.getAttributes() merges both, so after clearing a colour mid-line it
 * still reported the colour of the text to the left (#295).
 */
export function activeColor(editor: Editor, kind: 'highlight' | 'text'): string | null {
  const name = kind === 'highlight' ? 'highlight' : 'textStyle';
  const { state } = editor;
  if (state.selection.empty) {
    const marks = state.storedMarks ?? state.selection.$from.marks();
    const color = marks.find((m) => m.type.name === name)?.attrs.color;
    return typeof color === 'string' ? color : null;
  }
  if (kind === 'highlight' && !editor.isActive('highlight')) return null;
  const color = editor.getAttributes(name).color;
  return typeof color === 'string' ? color : null;
}

/** Remove character formatting from the selection. Links survive on purpose. */
export function clearFormatting(editor: Editor): void {
  let chain = editor.chain().focus();
  for (const mark of CLEARABLE_MARKS) {
    chain = chain.unsetMark(mark);
  }
  chain.run();
}
